import { PAD, PAD_BUTTONS, type AnalogState, type InputSnapshot, type PadButton } from '../emulator/types';

export type InputSource = 'touch' | 'keyboard' | 'gamepad';

export interface KeyMap {
  /** KeyboardEvent.code → PadButton */
  [code: string]: PadButton;
}

export const DEFAULT_KEYMAP: KeyMap = {
  ArrowUp: 'UP',
  ArrowDown: 'DOWN',
  ArrowLeft: 'LEFT',
  ArrowRight: 'RIGHT',
  KeyW: 'UP',
  KeyS: 'DOWN',
  KeyA: 'LEFT',
  KeyD: 'RIGHT',
  KeyX: 'A',
  KeyZ: 'B',
  KeyC: 'X',
  KeyV: 'Y',
  KeyQ: 'L',
  KeyE: 'R',
  Digit1: 'L2',
  Digit3: 'R2',
  Enter: 'START',
  ShiftRight: 'SELECT',
  Space: 'SELECT',
  Backspace: 'SELECT',
};

/**
 * 標準ゲームパッド (Gamepad API "standard" mapping) のボタン index → PadButton。
 * Xbox 系: 0=A 1=B 2=X 3=Y。SNES 配置に合わせて入れ替えてある。
 */
export const DEFAULT_GAMEPAD_MAP: Record<number, PadButton> = {
  0: 'B', // Xbox A / DualShock ×
  1: 'A', // Xbox B / DualShock ○
  2: 'Y', // Xbox X / DualShock □
  3: 'X', // Xbox Y / DualShock △
  4: 'L',
  5: 'R',
  6: 'L2',
  7: 'R2',
  8: 'SELECT',
  9: 'START',
  10: 'L3',
  // 11 (R3) は早送りホットキー専用。ゲーム入力には渡さない。
  12: 'UP',
  13: 'DOWN',
  14: 'LEFT',
  15: 'RIGHT',
};

const MAX_PORTS = 4;
const STICK_DEADZONE = 0.22;
/** アナログスティックを十字キーとしても扱う閾値 */
const STICK_TO_DPAD = 0.5;

export interface InputManagerOptions {
  keymap?: KeyMap;
  gamepadMap?: Record<number, PadButton>;
  /** 十字キーの斜め入力を許可するか（GB/NES 実機は同時押し不可の挙動が正しい場合がある） */
  allowDiagonals?: boolean;
  /** 相反する方向の同時押しを無効化する（ゲームによっては暴走の原因になる） */
  blockOpposingDirections?: boolean;
}

/**
 * タッチ / キーボード / ゲームパッドの 3 系統を 1 本のスナップショットに統合する。
 *
 * - 各ソースは独立したビットマスクを持ち、poll 時に OR で合成する。
 *   こうすると「タッチで押しっぱなし + キーボードで別ボタン」が破綻しない。
 * - 生成物 (InputSnapshot) は毎フレーム同じ TypedArray を使い回すので GC が走らない。
 */
export class InputManager {
  private touchMask = new Uint16Array(MAX_PORTS);
  private keyboardMask = new Uint16Array(MAX_PORTS);
  private gamepadMask = new Uint16Array(MAX_PORTS);
  private merged = new Uint16Array(MAX_PORTS);

  private touchAnalog: AnalogState = { leftX: 0, leftY: 0, rightX: 0, rightY: 0 };
  private padAnalog: AnalogState = { leftX: 0, leftY: 0, rightX: 0, rightY: 0 };
  private analog: AnalogState = { leftX: 0, leftY: 0, rightX: 0, rightY: 0 };

  private snapshot: InputSnapshot;

  private keymap: KeyMap;
  private gamepadMap: Record<number, PadButton>;
  private opts: Required<Omit<InputManagerOptions, 'keymap' | 'gamepadMap'>>;

  private listeners = new Set<(mask: number, source: InputSource) => void>();
  private keyboardAttached = false;
  private gamepadIndices: number[] = [];

  constructor(options: InputManagerOptions = {}) {
    this.keymap = options.keymap ?? DEFAULT_KEYMAP;
    this.gamepadMap = options.gamepadMap ?? DEFAULT_GAMEPAD_MAP;
    this.opts = {
      allowDiagonals: options.allowDiagonals ?? true,
      blockOpposingDirections: options.blockOpposingDirections ?? true,
    };
    this.snapshot = { buttons: this.merged, analog: this.analog };
  }

  /* ------------------------------------------------------------------ */
  /* タッチ (バーチャルパッド)                                           */
  /* ------------------------------------------------------------------ */

  setTouchButton(button: PadButton, pressed: boolean, port = 0): void {
    const b = 1 << PAD[button];
    const prev = this.touchMask[port] ?? 0;
    const next = pressed ? prev | b : prev & ~b;
    if (prev === next) return;
    this.touchMask[port] = next;
    this.emit(next, 'touch');
  }

  /** バーチャルパッドの十字キーをまとめて更新する（斜め入力用） */
  setTouchDirections(dirs: { up: boolean; down: boolean; left: boolean; right: boolean }, port = 0): void {
    let mask = this.touchMask[port] ?? 0;
    const DIRS = (1 << PAD.UP) | (1 << PAD.DOWN) | (1 << PAD.LEFT) | (1 << PAD.RIGHT);
    mask &= ~DIRS;
    if (dirs.up) mask |= 1 << PAD.UP;
    if (dirs.down) mask |= 1 << PAD.DOWN;
    if (dirs.left) mask |= 1 << PAD.LEFT;
    if (dirs.right) mask |= 1 << PAD.RIGHT;
    if (this.touchMask[port] === mask) return;
    this.touchMask[port] = mask;
    this.emit(mask, 'touch');
  }

  setTouchAnalog(stick: 'left' | 'right', x: number, y: number): void {
    if (stick === 'left') {
      this.touchAnalog.leftX = x;
      this.touchAnalog.leftY = y;
    } else {
      this.touchAnalog.rightX = x;
      this.touchAnalog.rightY = y;
    }
  }

  /** 画面から指が離れた/中断された時などに全解除する */
  releaseTouch(port = 0): void {
    if (this.touchMask[port] === 0) return;
    this.touchMask[port] = 0;
    this.touchAnalog.leftX = this.touchAnalog.leftY = 0;
    this.touchAnalog.rightX = this.touchAnalog.rightY = 0;
    this.emit(0, 'touch');
  }

  /* ------------------------------------------------------------------ */
  /* キーボード                                                          */
  /* ------------------------------------------------------------------ */

  attachKeyboard(target: Window | HTMLElement = window): () => void {
    if (this.keyboardAttached) return () => undefined;
    this.keyboardAttached = true;

    const onDown = (e: Event) => this.onKey(e as KeyboardEvent, true);
    const onUp = (e: Event) => this.onKey(e as KeyboardEvent, false);
    // ウィンドウからフォーカスが外れた時に押しっぱなしを残さない
    const onBlur = () => {
      this.keyboardMask.fill(0);
      this.emit(0, 'keyboard');
    };

    target.addEventListener('keydown', onDown);
    target.addEventListener('keyup', onUp);
    window.addEventListener('blur', onBlur);

    return () => {
      target.removeEventListener('keydown', onDown);
      target.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', onBlur);
      this.keyboardAttached = false;
    };
  }

  private onKey(e: KeyboardEvent, down: boolean): void {
    // 入力欄にフォーカスがあるときはゲーム入力にしない
    const el = document.activeElement;
    if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || (el as HTMLElement).isContentEditable)) {
      return;
    }
    const button = this.keymap[e.code];
    if (!button) return;
    e.preventDefault();
    if (e.repeat) return;

    const b = 1 << PAD[button];
    const prev = this.keyboardMask[0] ?? 0;
    const next = down ? prev | b : prev & ~b;
    if (prev === next) return;
    this.keyboardMask[0] = next;
    this.emit(next, 'keyboard');
  }

  setKeymap(map: KeyMap): void {
    this.keymap = map;
  }

  /* ------------------------------------------------------------------ */
  /* ゲームパッド                                                        */
  /* ------------------------------------------------------------------ */

  /**
   * Gamepad API はイベント駆動ではなくポーリング。
   * poll() の中から毎フレーム呼ぶ。
   */
  private pollGamepads(): void {
    const pads = navigator.getGamepads?.() ?? [];
    this.gamepadMask.fill(0);
    this.padAnalog.leftX = this.padAnalog.leftY = 0;
    this.padAnalog.rightX = this.padAnalog.rightY = 0;

    let port = 0;
    const indices: number[] = [];

    for (const pad of pads) {
      if (!pad || !pad.connected) continue;
      if (port >= MAX_PORTS) break;
      indices.push(pad.index);

      let mask = 0;
      for (let i = 0; i < pad.buttons.length; i++) {
        const button = this.gamepadMap[i];
        if (!button) continue;
        const gb = pad.buttons[i];
        // トリガーはアナログなので pressed だけでなく value も見る
        if (gb && (gb.pressed || gb.value > 0.5)) mask |= 1 << PAD[button];
      }

      const lx = applyDeadzone(pad.axes[0] ?? 0);
      const ly = applyDeadzone(pad.axes[1] ?? 0);
      const rx = applyDeadzone(pad.axes[2] ?? 0);
      const ry = applyDeadzone(pad.axes[3] ?? 0);

      // 左スティックは十字キーとしても効かせる（GB/NES で使えないと不便）
      if (lx <= -STICK_TO_DPAD) mask |= 1 << PAD.LEFT;
      if (lx >= STICK_TO_DPAD) mask |= 1 << PAD.RIGHT;
      if (ly <= -STICK_TO_DPAD) mask |= 1 << PAD.UP;
      if (ly >= STICK_TO_DPAD) mask |= 1 << PAD.DOWN;

      if (port === 0) {
        this.padAnalog.leftX = lx;
        this.padAnalog.leftY = ly;
        this.padAnalog.rightX = rx;
        this.padAnalog.rightY = ry;
      }

      this.gamepadMask[port] = mask;
      port++;
    }

    this.gamepadIndices = indices;
  }

  get connectedGamepads(): number {
    return this.gamepadIndices.length;
  }

  /** 対応していれば実機パッドを振動させる */
  rumble(durationMs = 120, strength = 0.6): void {
    const pads = navigator.getGamepads?.() ?? [];
    for (const pad of pads) {
      const actuator = (pad as Gamepad & { vibrationActuator?: { playEffect: (t: string, o: object) => Promise<void> } })
        ?.vibrationActuator;
      if (!actuator) continue;
      void actuator
        .playEffect('dual-rumble', {
          duration: durationMs,
          strongMagnitude: strength,
          weakMagnitude: strength * 0.5,
        })
        .catch(() => undefined);
    }
  }

  /* ------------------------------------------------------------------ */
  /* 合成                                                                */
  /* ------------------------------------------------------------------ */

  /** FrameDriver から毎フレーム呼ばれる。返り値は使い回されるので保持しないこと。 */
  poll = (): InputSnapshot => {
    this.pollGamepads();

    for (let p = 0; p < MAX_PORTS; p++) {
      let mask = (this.touchMask[p] ?? 0) | (this.keyboardMask[p] ?? 0) | (this.gamepadMask[p] ?? 0);

      if (this.opts.blockOpposingDirections) {
        const LR = (1 << PAD.LEFT) | (1 << PAD.RIGHT);
        const UD = (1 << PAD.UP) | (1 << PAD.DOWN);
        if ((mask & LR) === LR) mask &= ~LR;
        if ((mask & UD) === UD) mask &= ~UD;
      }

      if (!this.opts.allowDiagonals) {
        const horiz = mask & ((1 << PAD.LEFT) | (1 << PAD.RIGHT));
        const vert = mask & ((1 << PAD.UP) | (1 << PAD.DOWN));
        // 直近に押された方を優先したいが状態を持たないので縦を優先する
        if (horiz && vert) mask &= ~horiz;
      }

      this.merged[p] = mask;
    }

    // タッチ仮想スティックが動いていればそちらを優先、無ければ実機パッド
    const touchActive =
      Math.abs(this.touchAnalog.leftX) > 0.01 || Math.abs(this.touchAnalog.leftY) > 0.01;
    const src = touchActive ? this.touchAnalog : this.padAnalog;
    this.analog.leftX = src.leftX;
    this.analog.leftY = src.leftY;
    this.analog.rightX = src.rightX;
    this.analog.rightY = src.rightY;

    return this.snapshot;
  };

  /** 現在押されているボタン（UI のハイライト用） */
  currentMask(port = 0): number {
    return this.merged[port] ?? 0;
  }

  isPressed(button: PadButton, port = 0): boolean {
    return ((this.merged[port] ?? 0) & (1 << PAD[button])) !== 0;
  }

  onChange(fn: (mask: number, source: InputSource) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(mask: number, source: InputSource): void {
    for (const fn of this.listeners) fn(mask, source);
  }

  reset(): void {
    this.touchMask.fill(0);
    this.keyboardMask.fill(0);
    this.gamepadMask.fill(0);
    this.merged.fill(0);
  }
}

function applyDeadzone(v: number): number {
  if (Math.abs(v) < STICK_DEADZONE) return 0;
  // デッドゾーンの外側を 0..1 に再マップして段差をなくす
  const sign = v < 0 ? -1 : 1;
  return sign * ((Math.abs(v) - STICK_DEADZONE) / (1 - STICK_DEADZONE));
}

export { PAD_BUTTONS };
